import { ActionIcon as MantineActionIcon, ActionIconProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type HoverEffect = 'glow' | 'lift' | 'scale' | 'none';

export interface ThemedActionIconProps extends Omit<ActionIconProps, 'variant'> {
  variant?: 'gradient' | 'filled' | 'light' | 'outline' | 'subtle' | 'transparent' | 'default';
  gradientVariant?: GradientVariant;
  hoverEffect?: HoverEffect;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  title?: string;
}

const ActionIcon = forwardRef<HTMLButtonElement, ThemedActionIconProps>(
  ({ variant = 'subtle', gradientVariant = 'primary', hoverEffect = 'none', style, ...rest }, ref) => {
    const theme = useMantineTheme();

    const hoverStyles = {
      glow: {
        '--hover-shadow': `0 0 15px ${theme.other?.gradients?.[gradientVariant]?.from || theme.colors.blue[6]}60`,
      },
      lift: {
        '--hover-transform': 'translateY(-2px)',
        '--hover-shadow': '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
      scale: {
        '--hover-transform': 'scale(1.1)',
      },
      none: {},
    };

    if (variant !== 'gradient') {
      return (
        <MantineActionIcon
          ref={ref}
          variant={variant}
          style={{
            transition: 'all 0.2s ease',
            ...hoverStyles[hoverEffect],
            ...style,
          }}
          sx={{
            '&:hover': {
              transform: hoverStyles[hoverEffect]['--hover-transform'],
              boxShadow: hoverStyles[hoverEffect]['--hover-shadow'],
            },
          }}
          {...rest}
        />
      );
    }

    const gradient = theme.other.gradients[gradientVariant];

    return (
      <MantineActionIcon
        ref={ref}
        style={{
          backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
          color: 'white',
          border: 'none',
          transition: 'all 0.2s ease',
          ...hoverStyles[hoverEffect],
          ...style,
        }}
        sx={{
          '&:hover': {
            transform: hoverStyles[hoverEffect]['--hover-transform'],
            boxShadow: hoverStyles[hoverEffect]['--hover-shadow'],
          },
        }}
        {...rest}
      />
    );
  }
);

ActionIcon.displayName = 'ActionIcon';

export default ActionIcon;
